/**
 * Payroll runs: opening a month, locking inputs, calculating, reviewing, submitting, approving, and
 * the controlled reopen.
 *
 * ONE RUN, MANY CALCULATION VERSIONS, AND THAT IS THE WHOLE DESIGN. A run owns no numbers. It
 * points at an immutable input snapshot and at a calculation version, and every result and every
 * diagnostic belongs to a version. Recalculating adds a version; it never overwrites one. So:
 *   - a retried or duplicated job cannot corrupt a finished calculation (see `jobToken` on the
 *     model — a late job finds its version superseded and writes nothing);
 *   - an approval is bound to `approvedCalculationVersionId`, the exact version the approver saw,
 *     never to "the latest", so a recalculation after approval cannot smuggle new numbers through
 *     an old sign-off;
 *   - "recompute March" is meaningful, because the snapshot records WHICH rule versions, templates,
 *     attendance rows, tax elections and declarations it read.
 *
 * WHAT APPROVAL IS NOT. It authorises a version for payment PREPARATION. It is not a payment, it
 * does not publish or email a payslip, and it does not close the payroll month — all three live
 * under Payments & Payslips. This contract deliberately has no publish and no email route: finalize,
 * release, payslip publication and payment progress are `payroll-lifecycle.contract.ts` (ADR 0014),
 * and this contract only REPORTS them (`lifecycle`, and the `finalized` and `released` states).
 *
 * THREE KINDS OF BLOCKING, NOT ONE. A diagnostic's `diagnosticClass` says what it stops. An
 * invalid bank IFSC is a `payment_blocker`: the arithmetic is fine, the review should proceed, and
 * the money must not move. Only a `calculation_blocker` stops submission.
 *
 * NO REMEDIATION HAPPENS HERE. A diagnostic's `suggestedRemediation` is a sentence for a human.
 * Deferring an EWA recovery, waiving LOP or crediting leave are actions owned by the loan, payroll-
 * input and leave modules, and each goes through its own workflow. Overriding a warning records a
 * reason against the finding; it changes no number.
 *
 * CHECKSUMS ARE NOT SIGNATURES. `inputsChecksum` and `resultChecksum` identify a snapshot and a
 * result set — useful for spotting that two calculations ran over the same inputs. Neither proves
 * who produced them, and nothing here is cryptographically signed or dual-key encrypted.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CURRENCIES,
  PAYROLL_CALCULATION_STATES,
  PAYROLL_DIAGNOSTIC_CLASSES,
  PAYROLL_DIAGNOSTIC_SEVERITIES,
  PAYROLL_DIAGNOSTIC_STATES,
  PAYROLL_RUN_STATES,
  PAYROLL_VARIANCE_DRIVERS,
} from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { PAYMENT_STATUSES, PAYSLIP_STATUSES } from '../payroll-lifecycle.js';
import { payBasisSchema } from '../schemas/pay-basis.js';
import { payrollFactProvenanceSchema } from './payroll-rules.contract.js';
import { routeMeta } from './meta.js';


/**
 * Run steps reach the copilot through `payroll_run_step` (packages/ai), not as derived tools: each carries
 * `expectedVersion`, which a derived proposal freezes when it is made — so the second step of "lock and calculate" always
 * failed. The hand-written tool binds the version when the person approves. The screens call these routes as before.
 */
const RUN_STEP_AI = { ai: 'hidden' } as const;
const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const payrollRunStateSchema = z.enum(PAYROLL_RUN_STATES);
export const payrollCalculationStateSchema = z.enum(PAYROLL_CALCULATION_STATES);
export const diagnosticClassSchema = z.enum(PAYROLL_DIAGNOSTIC_CLASSES);
export const diagnosticSeveritySchema = z.enum(PAYROLL_DIAGNOSTIC_SEVERITIES);
export const diagnosticStateSchema = z.enum(PAYROLL_DIAGNOSTIC_STATES);
export const varianceDriverSchema = z.enum(PAYROLL_VARIANCE_DRIVERS);
const currencySchema = z.enum(CURRENCIES);

const runParams = z.object({ runId: uuidSchema });

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

/**
 * The five steps the screen shows. Derived from the run's state server-side, so the stepper is a
 * rendering of the backend's opinion rather than a second state machine that can disagree with it.
 */
export const runStepSchema = z.object({
  key: z.enum(['open_month', 'prepare_inputs', 'lock_and_calculate', 'review', 'signoff']),
  label: z.string(),
  status: z.enum(['done', 'active', 'blocked', 'upcoming']),
  /** "1,248 verified", "3 anomaly alerts" — one short line of context, computed. */
  detail: z.string().nullable(),
  at: instantSchema.nullable(),
});
export type RunStep = z.infer<typeof runStepSchema>;

export const calculationVersionSchema = z.object({
  id: uuidSchema,
  versionNo: z.number().int().positive(),
  state: payrollCalculationStateSchema,
  engineVersion: z.string(),
  snapshotVersionNo: z.number().int().positive(),
  attempt: z.number().int().positive(),
  queuedAt: instantSchema,
  startedAt: instantSchema.nullable(),
  completedAt: instantSchema.nullable(),
  failureReason: z.string().nullable(),
  headcount: z.number().int().nonnegative(),
  grossTotal: decimalStringSchema,
  netTotal: decimalStringSchema,
  statutoryTotal: decimalStringSchema,
  employerCostTotal: decimalStringSchema,
  taxTotal: decimalStringSchema,
  currency: currencySchema,
  /** Identifies this result set. NOT a signature — see this file's header. */
  resultChecksum: z.string().nullable(),
  isReconciled: z.boolean(),
  requestedByName: z.string().nullable(),
});
export type CalculationVersion = z.infer<typeof calculationVersionSchema>;

export const runScopeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('entity') }),
  z.object({ type: z.literal('selected'), employeeCount: z.number().int().positive() }),
]);
export type RunScope = z.infer<typeof runScopeSchema>;

export const runSummarySchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  payGroupId: uuidSchema,
  payGroupName: z.string(),
  fiscalYear: z.number().int(),
  cycleNo: z.number().int(),
  /** "October 2024" in the entity's timezone. */
  periodLabel: z.string(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  payDate: dateOnlySchema,
  currency: currencySchema,
  state: payrollRunStateSchema,
  headcount: z.number().int().nonnegative(),
  grossTotal: decimalStringSchema.nullable(),
  netTotal: decimalStringSchema.nullable(),
  openDiagnostics: z.number().int().nonnegative(),
  /**
   * Who this run pays. `entity`: every eligible employee of the legal entity (the default). `selected`: only the
   * employees chosen when the run was opened; `employeeCount` is how many.
   */
  scope: runScopeSchema,
  updatedAt: instantSchema,
});
export type RunSummary = z.infer<typeof runSummarySchema>;

/**
 * What finalize recorded about how it verified the version it locked. `engine`: the version was replayed
 * from its frozen rules and facts and matched. `legacy`: a 1.0.0 version has nothing to replay from
 * (ADR 0013 contract 7, unchanged), so it was finalized UNVERIFIED and `verified` is false with the
 * verifier's own reasons in `problems`.
 */
export const finalizationReplaySchema = z.object({
  basis: z.enum(['engine', 'legacy']),
  verified: z.boolean(),
  problems: z.array(z.string()),
});

export const runLifecycleSchema = z.object({
  finalizedAt: instantSchema.nullable(),
  finalizedByName: z.string().nullable(),
  finalizedCalculationVersionId: uuidSchema.nullable(),
  finalizationNote: z.string().nullable(),
  replay: finalizationReplaySchema.nullable(),
  releasedAt: instantSchema.nullable(),
  releasedByName: z.string().nullable(),
  payslipStatus: z.enum(PAYSLIP_STATUSES),
  payslipsPublishedAt: instantSchema.nullable(),
  paymentStatus: z.enum(PAYMENT_STATUSES),
  paymentStatusAt: instantSchema.nullable(),
});
export type RunLifecycle = z.infer<typeof runLifecycleSchema>;

export const runDetailSchema = z.object({
  ...runSummarySchema.shape,
  steps: z.array(runStepSchema),
  timezone: z.string(),
  /** The version on screen. Null before the first calculation finishes. */
  currentCalculation: calculationVersionSchema.nullable(),
  /** The version an approval is bound to, if any. Differs from the current one after a recalc. */
  approvedCalculation: calculationVersionSchema.nullable(),
  /**
   * True when a newer calculation exists than the one that was approved — the approval is stale and
   * the run needs a fresh one before release.
   */
  isApprovalStale: z.boolean(),
  calculationHistory: z.array(calculationVersionSchema),
  inputSnapshot: z
    .object({
      versionNo: z.number().int().positive(),
      employeeCount: z.number().int().nonnegative(),
      inputsChecksum: z.string(),
      capturedAt: instantSchema,
      capturedByName: z.string().nullable(),
      /** The exact versions the snapshot read. Rendered as a provenance list. */
      sources: z.array(z.object({ label: z.string(), value: z.string() })),
    })
    .nullable(),
  statutory: z
    .object({
      pfEmployee: decimalStringSchema,
      pfEmployer: decimalStringSchema,
      esiEmployee: decimalStringSchema,
      esiEmployer: decimalStringSchema,
      professionalTax: decimalStringSchema,
      tds: decimalStringSchema,
      currency: currencySchema,
    })
    .nullable(),
  headcountMovement: z.object({
    processed: z.number().int().nonnegative(),
    joiners: z.number().int().nonnegative(),
    exits: z.number().int().nonnegative(),
    onHold: z.number().int().nonnegative(),
  }),
  diagnosticCounts: z.object({
    calculationBlockers: z.number().int().nonnegative(),
    approvalWarnings: z.number().int().nonnegative(),
    paymentBlockers: z.number().int().nonnegative(),
    overridden: z.number().int().nonnegative(),
  }),
  teamSignoff: z.object({
    managersExpected: z.number().int().nonnegative(),
    managersSignedOff: z.number().int().nonnegative(),
    /** Whether the sign-offs are against the CURRENT snapshot — a re-lock invalidates them. */
    isCurrentSnapshot: z.boolean(),
  }),
  workflowInstanceId: uuidSchema.nullable(),
  submittedAt: instantSchema.nullable(),
  submittedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionComment: z.string().nullable(),
  reopenedAt: instantSchema.nullable(),
  reopenedByName: z.string().nullable(),
  reopenReason: z.string().nullable(),
  paymentInitiatedAt: instantSchema.nullable(),
  /**
   * Where the run stands beyond approval (ADR 0014). Every field is null / `not_*` until the run is
   * finalized, so a run that predates finalize reads exactly as it did.
   */
  lifecycle: runLifecycleSchema,
  /** What THIS caller may do next, from their real permissions and the run's real state. */
  actions: z.object({
    canLockInputs: z.boolean(),
    /** N17: the inputs are locked (or calculated) and not yet submitted — they can be reopened for editing. */
    canUnlockInputs: z.boolean().optional(),
    /** N20: not yet submitted — the run can be cancelled. */
    canCancel: z.boolean().optional(),
    canCalculate: z.boolean(),
    canSubmit: z.boolean(),
    submitBlockedReason: z.string().nullable(),
    canDecide: z.boolean(),
    canReopen: z.boolean(),
    reopenBlockedReason: z.string().nullable(),
    canOverrideDiagnostics: z.boolean(),
    canHoldSalary: z.boolean(),
    canSignOffTeam: z.boolean(),
    /** Finalize (`payroll.run:finalize`): the run is approved, its approved version is the current one. */
    canFinalize: z.boolean(),
    finalizeBlockedReason: z.string().nullable(),
    /** Release (`payroll.run:release`): the run is finalized. */
    canRelease: z.boolean(),
    releaseBlockedReason: z.string().nullable(),
  }),
  /** N20: set when the run was cancelled. */
  cancelledAt: instantSchema.nullable().optional(),
  cancelReason: z.string().nullable().optional(),
  /**
   * N19: inputs recorded AFTER the lock that the frozen snapshot does not contain — salary revisions, PF basis elections
   * and tax declarations taking effect in this period for people in the run. Null when nothing changed or nothing is
   * locked. Unlock (or re-lock) to include them.
   */
  inputsChangedSinceLock: z
    .object({
      employees: z.number().int().nonnegative(),
      compensation: z.number().int().nonnegative(),
      pfBasis: z.number().int().nonnegative(),
      taxDeclarations: z.number().int().nonnegative(),
      /** People with unpaid leave approved or cancelled after the lock (it changes loss of pay). */
      unpaidLeave: z.number().int().nonnegative().optional(),
      /** People whose attendance in the period was corrected after the lock (a regularisation,
       *  a verification, a late punch), which can change loss of pay too. */
      attendance: z.number().int().nonnegative().optional(),
    })
    .nullable()
    .optional(),
  version: z.number().int().positive(),
});
export type RunDetail = z.infer<typeof runDetailSchema>;

export const listRunsQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  payGroupId: uuidSchema.optional(),
  fiscalYear: z.coerce.number().int().min(2000).max(2100).optional(),
  state: payrollRunStateSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(5).max(50).default(12),
});

export const runListSchema = z.object({
  items: z.array(runSummarySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Cycles with no run yet, offered as "open this month". */
  openableCycles: z.array(
    z.object({
      calendarCycleId: uuidSchema,
      payGroupId: uuidSchema,
      payGroupName: z.string(),
      legalEntityId: uuidSchema,
      legalEntityName: z.string(),
      fiscalYear: z.number().int(),
      cycleNo: z.number().int(),
      periodLabel: z.string(),
      periodStart: dateOnlySchema,
      payDate: dateOnlySchema,
      /** False while the cycle's attendance cut-off has not passed. */
      isCutoffPassed: z.boolean(),
    }),
  ),
});
export type RunList = z.infer<typeof runListSchema>;

/** The most employees a "selected employees" run may name. Larger sets are a whole-entity run. */
export const MAX_SELECTED_RUN_EMPLOYEES = 500;

export const openRunBodySchema = z.object({
  calendarCycleId: uuidSchema,
  /**
   * Optional. Omitted: the run covers every eligible employee of the legal entity (unchanged behaviour). Present:
   * the run covers ONLY these employments, each of which must belong to the cycle's legal entity, be eligible for
   * the period, and not already be paid by another open run for the same cycle.
   */
  employmentIds: z
    .array(uuidSchema)
    .min(1)
    .max(MAX_SELECTED_RUN_EMPLOYEES)
    .refine((ids) => new Set(ids).size === ids.length, 'Each employee may be listed once')
    .optional(),
});

/**
 * Lock the inputs and capture a snapshot. Separate from calculating, deliberately: locking is what
 * makes the inputs quotable, and a recalculation over the SAME locked inputs must not silently
 * pick up an attendance edit made in between.
 */
/** N17: back to draft so inputs can be edited; the snapshot and every calculation are kept as history. */
export const unlockInputsBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
});

/** N20: abandon a run before it is submitted. Reservations it held are released; its month can be opened again. */
export const cancelRunBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(10).max(500),
});

export const lockInputsBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  /** Re-locking after a reopen: captures a new snapshot and invalidates team sign-offs. */
  note: z.string().trim().max(300).nullable().optional(),
});

export const calculateBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  /**
   * Re-run against the existing snapshot rather than the latest one. The default (false) is a plain
   * calculation of the current snapshot; either way a NEW version is created.
   */
  recalculate: z.boolean().default(false),
});

/** 202 — the calculation runs on the worker. Poll `getRun` or the version below. */
export const calculationAcceptedSchema = z.object({
  runId: uuidSchema,
  calculationVersionId: uuidSchema,
  versionNo: z.number().int().positive(),
  state: payrollCalculationStateSchema,
  queuedAt: instantSchema,
});

// ---------------------------------------------------------------------------
// The input snapshot
// ---------------------------------------------------------------------------

/**
 * The captured inputs, as stored on `PayrollRunInputSnapshot.inputs`.
 *
 * DECLARED HERE, IN THE CONTRACT, THOUGH NO ROUTE RETURNS IT. The API writes this JSON and the
 * WORKER reads it, in a different process, deployed separately. That is exactly the shape of
 * problem a shared schema exists for: a snapshot written by yesterday's API is still in the
 * database when today's worker picks it up, and a consumer that casts rather than parses turns a
 * field rename into an `undefined` inside someone's net pay. The worker parses.
 */
export const snapshotEmployeeSchema = z.object({
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string(),
  personName: z.string(),
  designation: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  currency: currencySchema,
  periodDays: decimalStringSchema,
  /** The EFFECTIVE figure the payroll engine prorates pay against — after any EarlyExitPenaltyPolicy
   * monthly cap and any approved EarlyExitWaiverRequest have been applied to the early-exit
   * component. Never derived by rewriting AttendanceDay; see `lopDaysFor` in
   * apps/api/src/lib/payroll-run-snapshot.ts. */
  lopDays: decimalStringSchema,
  /** The pre-cap, pre-waiver total — what `lopDays` would be with no policy cap and no waiver
   * applied. Equal to `lopDays` whenever neither is in play. Kept so a payroll reviewer can see
   * that a cap/waiver actually changed something, not just trust a single opaque number.
   *
   * OPTIONAL BECAUSE OF SNAPSHOTS ALREADY IN THE DATABASE (as `employmentWindow` below). A snapshot locked before this key
   * existed does not have it, and the worker PARSES every snapshot it calculates, so a required key made every earlier
   * snapshot unreadable. Absence means "no cap or waiver was recorded": read it as equal to `lopDays`. Diagnostic only; no
   * calculation reads it. */
  rawLopDays: decimalStringSchema.optional(),
  isNewJoiner: z.boolean(),
  isExiting: z.boolean(),
  /**
   * The pay period's calendar bounds and this employment's window inside them — what makes a
   * mid-month joiner or leaver a PART month. `isNewJoiner` / `isExiting` say only THAT the window
   * is short; the calculation needs to know BY HOW MUCH, and before this field existed it had no
   * way to, so both were paid a full month.
   *
   * OPTIONAL BECAUSE OF SNAPSHOTS ALREADY IN THE DATABASE. A snapshot locked before this key
   * existed does not have it, and absence means "the whole period" — exactly the basis those
   * snapshots were calculated on, so re-running one reproduces its figures rather than silently
   * restating an approved payroll. A run picks the correction up when its inputs are locked again.
   *
   * The period bounds are repeated here rather than read from the snapshot root so that one
   * employee's row is self-describing: every consumer that prorates is handed an employee, not a
   * snapshot, and a window assembled from two places is a window that can be assembled wrongly.
   */
  employmentWindow: z
    .object({
      periodStart: dateOnlySchema,
      periodEnd: dateOnlySchema,
      joinedOn: dateOnlySchema,
      /** Null while the employment is open-ended. */
      lastWorkingDay: dateOnlySchema.nullable(),
    })
    .optional(),
  /** Days of leave approved AFTER the attendance cut-off — a diagnostic, not a pay input. */
  postCutoffLeaveDays: decimalStringSchema,
  hasBankAccount: z.boolean(),
  bankAccountIfsc: z.string().nullable(),
  /**
   * Pay in whole rupees (the entity's `roundPayToRupee`): earnings, PF, PT, LWF and TDS rounded to the unit. Absent on a
   * snapshot locked before the setting existed, which replays in paise as it was calculated.
   */
  roundToUnit: z.boolean().optional(),
  structure: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      componentType: z.string(),
      monthlyAmount: decimalStringSchema,
      proRates: z.boolean(),
      taxability: z.string(),
      /** False when the component is outside the ESI wage (`inEsiWage`). Absent: counts, as on older snapshots. */
      inEsiWage: z.boolean().optional(),
    }),
  ),
  variablePay: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      amount: decimalStringSchema,
      /**
       * How the ESI Act treats this one-off line (Section 2(9), 2(22)): `excluded` — not wages at all (an annual bonus, paid at
       * intervals of more than two months; a reimbursed expense); `contribution_only` — wages for the contribution but left
       * out of the ₹21,000 eligibility test (overtime). Absent: wages for both, as every line was before.
       */
      esiWage: z.enum(['excluded', 'contribution_only']).optional(),
    }),
  ),
  reimbursements: z.array(
    z.object({ code: z.string(), name: z.string(), amount: decimalStringSchema }),
  ),
  recoveries: z.array(
    z.object({ code: z.string(), name: z.string(), amount: decimalStringSchema }),
  ),
  statutory: z.object({
    pf: z.object({
      isApplicable: z.boolean(),
      employeeRatePercent: decimalStringSchema,
      employerRatePercent: decimalStringSchema,
      wageCeiling: decimalStringSchema.nullable(),
    }),
    esi: z.object({
      isApplicable: z.boolean(),
      employeeRatePercent: decimalStringSchema,
      employerRatePercent: decimalStringSchema,
      grossEligibilityLimit: decimalStringSchema.nullable(),
      /**
       * Covered earlier in this ESI contribution period (Apr–Sep, Oct–Mar), so ESI continues to the period's end whatever
       * the wage (ESI Act, Section 2(9) proviso). Absent on older snapshots: the monthly limit test alone, as then.
       */
      continuesThisPeriod: z.boolean().optional(),
    }),
    professionalTax: z.object({
      isApplicable: z.boolean(),
      /** The slab for the FULL-MONTH salary. Used only by a snapshot without `slabs` (locked before they were frozen). */
      monthlyAmount: decimalStringSchema,
      /**
       * The employee's table for this month (state, month and gender variant already chosen). The engine reads it against
       * the gross EARNED this month, as the state Acts charge on the month's salary: a part month or a month without pay
       * falls to its own slab. Absent on older snapshots, which replay with `monthlyAmount`.
       */
      slabs: z
        .array(
          z.object({
            fromAmount: decimalStringSchema,
            toAmount: decimalStringSchema.nullable(),
            monthlyTax: decimalStringSchema,
          }),
        )
        .optional(),
    }),
    /**
     * Labour Welfare Fund for THIS month (G2): a flat state levy deducted only in the rule's months (Maharashtra: June and
     * December). `isApplicable` is false in every other month. Absent on snapshots locked before it existed.
     */
    lwf: z
      .object({
        isApplicable: z.boolean(),
        employeeAmount: decimalStringSchema,
        employerAmount: decimalStringSchema,
      })
      .optional(),
  }),
  tax: z.object({
    regime: z.enum(['old', 'new']),
    /** The declaration revision read, so the snapshot names what it used. */
    declarationRevisionNo: z.number().int().nullable(),
    declaredItems: z.array(
      z.object({
        categoryCode: z.string(),
        declaredAmount: decimalStringSchema,
        verifiedAmount: decimalStringSchema.nullable(),
        inputs: z.record(z.unknown()),
      }),
    ),
    salaryBasis: z.object({
      annualBasic: decimalStringSchema,
      annualDearnessAllowance: decimalStringSchema,
      annualHraReceived: decimalStringSchema,
    }),
    projectedAnnualGross: decimalStringSchema,
    taxDeductedYtd: decimalStringSchema,
    monthsRemaining: z.number().int().min(1).max(12),
    /**
     * Employee PF and professional tax already deducted this fiscal year, before this month: opening balance plus this
     * system's approved runs. The engine adds this month's figures for the months left (B31). Optional: absent on a
     * snapshot locked before it existed, which replays as before.
     */
    payrollSourcedYtd: z
      .object({
        employeeProvidentFund: decimalStringSchema,
        professionalTax: decimalStringSchema,
      })
      .optional(),
    /** Extra TDS entered for this month (`additional_tds` inputs), on top of the calculated TDS. Absent: none. */
    additionalTds: decimalStringSchema.optional(),
  }),
  /** The prior finalised run's net for this employee, for the swing diagnostic. */
  priorNetPayable: decimalStringSchema.nullable(),
  /**
   * The employee's work state when it has no professional-tax rule while the entity has rules for other states: no PT
   * was deducted, and the run raises PT_STATE_NOT_CONFIGURED. Absent on older snapshots and when there is nothing to say.
   */
  ptStateUnconfigured: z.string().nullable().optional(),
  /**
   * ENGINE FACTS (ADR 0012, phase 4b): the facts a rule-engine calculation of this employee needs, frozen at
   * lock with the existing fact-provenance shape — name, kind, state (`present` or `absent`), value, source,
   * source reference, capture time.
   *
   *   - FACTS, NOT RULES OR CONFIGURATION. Rules live in the run's rules snapshot; this holds only the values the
   *     rules are evaluated over, and where each came from.
   *   - PRESENT ONLY ON AN ENGINE-MODE SNAPSHOT. On every legacy and shadow snapshot the key is ABSENT (not an
   *     empty list), so those snapshots and their checksums are unchanged.
   *   - A fact nothing supplied is recorded ABSENT (its value null), never defaulted or fabricated; the engine
   *     then refuses to calculate that employee rather than guess.
   *   - COVERED BY `inputsChecksum` like the rest of `inputs`.
   *
   * Replay is `frozen facts + frozen rules + engine version`: it never reads a live employee or configuration
   * table. The tax facts (`taxableEarningsYtd`, `taxDeductedYtd`, `monthsRemaining`, `eligibleDeductions`,
   * `regime`) are recorded the same way, when the composition requires them.
   */
  engineFacts: z.array(payrollFactProvenanceSchema).optional(),
  /**
   * PAY BASIS (ADR 0015): which of CTC, gross or net this employee's pay was specified as, FROZEN at lock beside
   * their engine facts. It decides which reverse calculation the engine runs and which facts it requires, so a
   * replay never re-derives it from today's compensation.
   *
   * ABSENT MEANS CTC. Every snapshot written before ADR 0015 lacks the key and was calculated from CTC, so absence
   * is not "unknown": it is exactly that basis, and those snapshots replay bit for bit as they always did (pinned by
   * a test). Only an engine-mode lock writes it.
   */
  payBasis: payBasisSchema.optional(),
});
export type SnapshotEmployee = z.infer<typeof snapshotEmployeeSchema>;

export const runInputSnapshotSchema = z.object({
  runId: uuidSchema,
  legalEntityId: uuidSchema,
  payGroupId: uuidSchema,
  fiscalYear: z.number().int(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  currency: currencySchema,
  timezone: z.string(),
  /** The whole tax-year configuration, copied in. A later publish cannot rewrite this month. */
  taxRules: z.record(z.unknown()),
  employees: z.array(snapshotEmployeeSchema),
});
export type RunInputSnapshot = z.infer<typeof runInputSnapshotSchema>;

/** The identities behind the values — what "recompute March" needs to mean anything. */
export const snapshotSourcesSchema = z.object({
  taxYearConfigId: uuidSchema.nullable(),
  taxYearConfigVersionNo: z.number().int().nullable(),
  statutoryRuleVersionIds: z.array(uuidSchema),
  ctcTemplateIds: z.array(uuidSchema),
  payGroupRuleVersionNo: z.number().int(),
  /** The instant attendance was read as of. */
  attendanceReadAt: instantSchema,
  compensationAsOf: dateOnlySchema,
  declarationRevisions: z.array(
    z.object({ employmentId: uuidSchema, revisionNo: z.number().int().nullable() }),
  ),
  priorRunId: uuidSchema.nullable(),
  priorCalculationVersionId: uuidSchema.nullable(),
});
export type SnapshotSources = z.infer<typeof snapshotSourcesSchema>;

/** The result component breakdown stored on `PayrollEmployeeResult.components`. */
export const resultComponentSchema = z.object({
  code: z.string(),
  name: z.string(),
  componentType: z.string(),
  amount: decimalStringSchema,
  basis: z.string().nullable(),
});
export type ResultComponentRecord = z.infer<typeof resultComponentSchema>;

// ---------------------------------------------------------------------------
// Diagnostics
// ---------------------------------------------------------------------------

export const diagnosticSchema = z.object({
  id: uuidSchema,
  ruleCode: z.string(),
  diagnosticClass: diagnosticClassSchema,
  severity: diagnosticSeveritySchema,
  title: z.string(),
  detail: z.string(),
  state: diagnosticStateSchema,
  employmentId: uuidSchema.nullable(),
  personName: z.string().nullable(),
  employeeCode: z.string().nullable(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  /** The employee's computed net under the current version, for context. */
  computedNetPayable: decimalStringSchema.nullable(),
  impactAmount: decimalStringSchema.nullable(),
  currency: currencySchema,
  /** Advisory text. Nothing acts on it — see this file's header. */
  suggestedRemediation: z.string().nullable(),
  /**
   * Where to go to fix it. `module` is resolved to a real screen by the client's own route map;
   * the API does not hand out URLs.
   */
  fixTarget: z
    .object({
      module: z.enum(['payroll_inputs', 'payroll_profiles', 'attendance', 'leave', 'loans']),
      employmentId: uuidSchema.nullable(),
      label: z.string(),
    })
    .nullable(),
  isOverridable: z.boolean(),
  /** Only the named, deterministic fixes a rule declares. Empty for most rules. */
  autoResolutions: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      description: z.string(),
    }),
  ),
  isHeld: z.boolean(),
  overriddenByName: z.string().nullable(),
  overriddenAt: instantSchema.nullable(),
  overrideReason: z.string().nullable(),
});
export type Diagnostic = z.infer<typeof diagnosticSchema>;

export const listDiagnosticsQuerySchema = z.object({
  diagnosticClass: diagnosticClassSchema.optional(),
  severity: diagnosticSeveritySchema.optional(),
  state: diagnosticStateSchema.optional(),
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(25),
});

export const diagnosticListSchema = z.object({
  items: z.array(diagnosticSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  calculationVersionId: uuidSchema,
  versionNo: z.number().int().positive(),
  /** How many statutory parameters the engine evaluated — what the run was checked against. */
  rulesEvaluated: z.number().int().nonnegative(),
  counts: z.object({
    calculationBlockers: z.number().int().nonnegative(),
    approvalWarnings: z.number().int().nonnegative(),
    paymentBlockers: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    overridden: z.number().int().nonnegative(),
  }),
});
export type DiagnosticList = z.infer<typeof diagnosticListSchema>;

/** Accepting a warning as-is. Records a reason against the finding; changes no number. */
export const overrideDiagnosticBodySchema = z.object({
  reason: z.string().trim().min(10).max(500),
});

/**
 * Apply one NAMED fix a rule declares. There is no "resolve everything" — a blanket action over
 * findings nobody read is how a payroll error becomes a payroll incident.
 */
export const applyAutoResolutionBodySchema = z.object({
  resolutionCode: z.string().min(1).max(60),
});

/** Withhold one employee from PAYMENT. Their pay is still calculated and still shown. */
export const holdSalaryBodySchema = z.object({
  employmentId: uuidSchema,
  reason: z.string().trim().min(10).max(500),
  ruleCode: z.string().max(60).nullable().optional(),
});

export const releaseSalaryHoldBodySchema = z.object({
  holdId: uuidSchema,
  note: z.string().trim().max(500).nullable().optional(),
});

export const salaryHoldSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  reason: z.string(),
  ruleCode: z.string().nullable(),
  heldByName: z.string().nullable(),
  heldAt: instantSchema,
  releasedAt: instantSchema.nullable(),
  releasedByName: z.string().nullable(),
});
export type SalaryHold = z.infer<typeof salaryHoldSchema>;

// ---------------------------------------------------------------------------
// Month-over-month variance
// ---------------------------------------------------------------------------

/**
 * The variance bridge, against a NAMED prior run — the previous finalised run for the same pay
 * group in the same currency, not "last month" by date arithmetic.
 *
 * IT RECONCILES EXACTLY. `drivers` sums to `netDelta` to the paisa, and `other` is the reconciling
 * remainder rather than a rounding gap. `netDeltaCompact` is a rounded label for the headline;
 * `netDelta` is the precise figure, and the two are separate fields so a display rounding can never
 * be mistaken for the real number.
 */
export const varianceSchema = z.object({
  calculationVersionId: uuidSchema,
  currency: currencySchema,
  comparedAgainst: z
    .object({
      runId: uuidSchema,
      calculationVersionId: uuidSchema,
      periodLabel: z.string(),
      netTotal: decimalStringSchema,
      approvedAt: instantSchema.nullable(),
    })
    .nullable(),
  /** Why there is nothing to compare against, when `comparedAgainst` is null. */
  unavailableReason: z.string().nullable(),
  currentNetTotal: decimalStringSchema,
  netDelta: decimalStringSchema,
  netDeltaPercent: decimalStringSchema,
  drivers: z.array(
    z.object({
      driver: varianceDriverSchema,
      label: z.string(),
      amount: decimalStringSchema,
      headcount: z.number().int(),
      detail: z.string().nullable(),
    }),
  ),
  byDepartment: z.array(
    z.object({
      departmentId: uuidSchema.nullable(),
      departmentName: z.string(),
      currentTotal: decimalStringSchema,
      priorTotal: decimalStringSchema,
      delta: decimalStringSchema,
      deltaPercent: decimalStringSchema,
    }),
  ),
  /** True when `drivers` sums exactly to `netDelta`. False is a bug, and says so on screen. */
  isReconciled: z.boolean(),
});
export type Variance = z.infer<typeof varianceSchema>;

// ---------------------------------------------------------------------------
// Payslip register & preview
// ---------------------------------------------------------------------------

export const registerQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  departmentId: uuidSchema.optional(),
  onlyHeld: z.coerce.boolean().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(10).max(200).default(25),
});

export const registerRowSchema = z.object({
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  payableDays: decimalStringSchema,
  lopDays: decimalStringSchema,
  fixedGross: decimalStringSchema,
  variablePay: decimalStringSchema,
  totalDeductions: decimalStringSchema,
  netPayable: decimalStringSchema,
  currency: currencySchema,
  isHeld: z.boolean(),
  /** Findings on this employee, so the register shows why a row looks odd. */
  diagnosticCount: z.number().int().nonnegative(),
});
export type RegisterRow = z.infer<typeof registerRowSchema>;

export const registerSchema = z.object({
  items: z.array(registerRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  calculationVersionId: uuidSchema,
  versionNo: z.number().int().positive(),
  /**
   * Whether this version is approved. The register labels itself DRAFT while it is not, because an
   * unapproved payslip is a preview of a proposal, not a statement of pay.
   */
  isApproved: z.boolean(),
  currency: currencySchema,
});
export type Register = z.infer<typeof registerSchema>;

export const payslipComponentSchema = z.object({
  code: z.string(),
  name: z.string(),
  /** earning | deduction | employer_contribution | reimbursement | perquisite | informational */
  componentType: z.string(),
  amount: decimalStringSchema,
  /** How it was derived, e.g. "40% of CTC", "Pro-rated for 22 of 31 days". */
  basis: z.string().nullable(),
});

export const payslipPreviewSchema = z.object({
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  periodLabel: z.string(),
  payDate: dateOnlySchema,
  payableDays: decimalStringSchema,
  lopDays: decimalStringSchema,
  earnings: z.array(payslipComponentSchema),
  deductions: z.array(payslipComponentSchema),
  /** Shown only where the caller may see employer cost — it is not on an employee's own payslip. */
  employerContributions: z.array(payslipComponentSchema),
  grossEarnings: decimalStringSchema,
  totalDeductions: decimalStringSchema,
  employerContributionTotal: decimalStringSchema,
  netPayable: decimalStringSchema,
  currency: currencySchema,
  taxRegime: z.enum(['old', 'new']).nullable(),
  /** True until this version is approved AND published. Watermarks the preview and the PDF. */
  isDraft: z.boolean(),
  /** Why it is a draft: "not yet approved", "approved but not published". */
  draftReason: z.string().nullable(),
  isHeld: z.boolean(),
  holdReason: z.string().nullable(),
  versionNo: z.number().int().positive(),
});
export type PayslipPreview = z.infer<typeof payslipPreviewSchema>;

// ---------------------------------------------------------------------------
// Team sign-off
// ---------------------------------------------------------------------------

export const teamExceptionSchema = z.object({
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  /** What a manager can actually speak to: attendance, LOP, overtime, variable pay. */
  category: z.enum(['lop', 'attendance', 'overtime', 'variable_pay', 'unpaid_leave']),
  title: z.string(),
  detail: z.string(),
  /**
   * Deliberately no salary figure. A manager signs off INPUTS; their report's net pay is not
   * theirs to see, and `payroll:read` is not a permission a plain manager holds.
   */
  days: decimalStringSchema.nullable(),
  hours: decimalStringSchema.nullable(),
});
export type TeamException = z.infer<typeof teamExceptionSchema>;

export const teamSignoffViewSchema = z.object({
  runId: uuidSchema,
  periodLabel: z.string(),
  payDate: dateOnlySchema,
  teamSize: z.number().int().nonnegative(),
  exceptions: z.array(teamExceptionSchema),
  snapshotVersion: z.number().int().nonnegative(),
  signedOffAt: instantSchema.nullable(),
  /** False once a re-lock produced a newer snapshot than the one signed off. */
  isSignoffCurrent: z.boolean(),
  canSignOff: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type TeamSignoffView = z.infer<typeof teamSignoffViewSchema>;

export const signOffTeamBodySchema = z.object({
  note: z.string().trim().max(500).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Submission, approval, reopen
// ---------------------------------------------------------------------------

export const submitRunBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  /** The version being submitted, named explicitly so a concurrent recalc cannot redirect it. */
  calculationVersionId: uuidSchema,
  note: z.string().trim().max(1000).nullable().optional(),
});

/** What the approver sees before deciding — the summary, the variance, and what is unresolved. */
export const approvalViewSchema = z.object({
  runId: uuidSchema,
  periodLabel: z.string(),
  legalEntityName: z.string(),
  payGroupName: z.string(),
  state: payrollRunStateSchema,
  calculation: calculationVersionSchema,
  submittedAt: instantSchema.nullable(),
  submittedByName: z.string().nullable(),
  submissionNote: z.string().nullable(),
  variance: varianceSchema.nullable(),
  /** Every warning still open or overridden, so nothing is approved unseen. */
  unresolvedWarnings: z.array(diagnosticSchema),
  paymentBlockers: z.array(diagnosticSchema),
  salaryHolds: z.array(salaryHoldSchema),
  /** The configured maker–checker rule, enforced on the backend, shown here for honesty. */
  makerChecker: z.object({
    isEnforced: z.boolean(),
    description: z.string(),
    submitterName: z.string().nullable(),
    approverName: z.string().nullable(),
  }),
  canDecide: z.boolean(),
  cannotDecideReason: z.string().nullable(),
});
export type ApprovalView = z.infer<typeof approvalViewSchema>;

export const decideRunBodySchema = z
  .object({
    /** Binds the decision to the exact version reviewed. A mismatch is a 409, never an approval. */
    calculationVersionId: uuidSchema,
    decision: z.enum(['approved', 'changes_requested']),
    comment: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.decision === 'changes_requested' && !b.comment?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Say what needs changing — HR cannot act on a rejection with no reason',
        path: ['comment'],
      });
    }
  });

/**
 * The authorised unlock. Preserves every prior result, invalidates the approval, and requires a
 * fresh calculation and a fresh approval.
 *
 * REFUSED ONCE PAYMENT HAS STARTED. If `paymentInitiatedAt` is set, money is already moving and an
 * unlock back to draft would leave the ledger and the run disagreeing. That case needs an explicit
 * correction or reversal under Payments & Payslips, and this route says so rather than pretending.
 */
export const reopenRunBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(20).max(1000),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const payrollRunsContract = c.router(
  {
    listRuns: {
      method: 'GET',
      path: '/runs',
      query: listRunsQuerySchema,
      responses: {
        200: runListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Payroll runs in scope, plus the cycles that can still be opened',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    openRun: {
      method: 'POST',
      path: '/runs',
      headers: idempotencyHeadersSchema,
      body: openRunBodySchema,
      responses: {
        201: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A run already exists for that cycle, or a selected employee is already in another open run of it. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Open a payroll month for one pay-calendar cycle',
      metadata: routeMeta('payroll.run:create'),
    },

    getRun: {
      method: 'GET',
      path: '/runs/:runId',
      pathParams: runParams,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One run: its state, its stepper, its totals and its provenance',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    lockInputs: {
      method: 'POST',
      path: '/runs/:runId/lock-inputs',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: lockInputsBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** Required inputs are missing — the response names them. */
        422: errorBodySchema,
      },
      summary: 'Lock the inputs and capture an immutable snapshot',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    unlockInputs: {
      method: 'POST',
      path: '/runs/:runId/unlock-inputs',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: unlockInputsBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reopen the inputs of a locked or calculated run that is not yet submitted',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    cancelRun: {
      method: 'POST',
      path: '/runs/:runId/cancel',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: cancelRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Cancel a run that is not yet submitted; its month can be opened again',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    calculate: {
      method: 'POST',
      path: '/runs/:runId/calculate',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: calculateBodySchema,
      responses: {
        202: calculationAcceptedSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A calculation is already running, or the run is submitted. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Queue a calculation of the locked snapshot as a new version',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], RUN_STEP_AI),
    },

    listDiagnostics: {
      method: 'GET',
      path: '/runs/:runId/diagnostics',
      pathParams: runParams,
      query: listDiagnosticsQuerySchema,
      responses: {
        200: diagnosticListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The current calculation version's findings",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    rerunDiagnostics: {
      method: 'POST',
      path: '/runs/:runId/diagnostics/rerun',
      pathParams: runParams,
      body: z.object({}),
      responses: {
        200: diagnosticListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Re-evaluate the diagnostic rules against the current version',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { salaryPin: 'others' }),
    },

    overrideDiagnostic: {
      method: 'POST',
      path: '/runs/:runId/diagnostics/:diagnosticId/override',
      pathParams: runParams.extend({ diagnosticId: uuidSchema }),
      body: overrideDiagnosticBodySchema,
      responses: {
        200: diagnosticSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not an overridable rule — a calculation blocker has no reasonable override. */
        409: errorBodySchema,
      },
      summary: 'Accept one overridable warning, with a recorded reason',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { salaryPin: 'others' }),
    },

    applyAutoResolution: {
      method: 'POST',
      path: '/runs/:runId/diagnostics/:diagnosticId/resolve',
      pathParams: runParams.extend({ diagnosticId: uuidSchema }),
      body: applyAutoResolutionBodySchema,
      responses: {
        200: diagnosticSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Apply one named, deterministic fix a rule declares',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { salaryPin: 'others' }),
    },

    holdSalary: {
      method: 'POST',
      path: '/runs/:runId/holds',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: holdSalaryBodySchema,
      responses: {
        201: salaryHoldSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Withhold one employee's payment within this run, with a reason",
      metadata: routeMeta(['payroll.run:execute', 'payroll:read']),
    },

    releaseSalaryHold: {
      method: 'POST',
      path: '/runs/:runId/holds/release',
      pathParams: runParams,
      body: releaseSalaryHoldBodySchema,
      responses: {
        200: salaryHoldSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Release a salary hold',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read']),
    },

    listSalaryHolds: {
      method: 'GET',
      path: '/runs/:runId/holds',
      pathParams: runParams,
      responses: {
        200: z.object({ items: z.array(salaryHoldSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "This run's salary holds",
      metadata: routeMeta('payroll:read'),
    },

    getVariance: {
      method: 'GET',
      path: '/runs/:runId/variance',
      pathParams: runParams,
      responses: {
        200: varianceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The month-over-month bridge against the prior finalised run',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    getRegister: {
      method: 'GET',
      path: '/runs/:runId/register',
      pathParams: runParams,
      query: registerQuerySchema,
      responses: {
        200: registerSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The current version's payslip register, paginated",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    getPayslipPreview: {
      method: 'GET',
      path: '/runs/:runId/register/:employmentId',
      pathParams: runParams.extend({ employmentId: uuidSchema }),
      responses: {
        200: payslipPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One employee's computed payslip, from the persisted result",
      metadata: routeMeta('payroll:read', { salaryPin: 'subject' }),
    },

    getTeamSignoff: {
      method: 'GET',
      path: '/runs/:runId/team-signoff',
      pathParams: runParams,
      responses: {
        200: teamSignoffViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A manager's own team exceptions and sign-off state for this run",
      metadata: routeMeta('payroll.run.signoff:team'),
    },

    signOffTeam: {
      method: 'POST',
      path: '/runs/:runId/team-signoff',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: signOffTeamBodySchema,
      responses: {
        200: teamSignoffViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Sign off one's own team's payroll inputs for this run",
      metadata: routeMeta(['payroll.run.signoff:team', 'payroll:read']),
    },

    submitRun: {
      method: 'POST',
      path: '/runs/:runId/submit',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: submitRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale version, a calculation blocker outstanding, or already submitted. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Submit one calculation version for approval',
      metadata: routeMeta(['payroll.run:execute', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    getApprovalView: {
      method: 'GET',
      path: '/runs/:runId/approval',
      pathParams: runParams,
      responses: {
        200: approvalViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'What the approver reviews: totals, variance, and everything unresolved',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    decideRun: {
      method: 'POST',
      path: '/runs/:runId/decision',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: decideRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The version moved on, or maker–checker refuses this approver. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Approve a calculation version, or send it back for changes',
      metadata: routeMeta(['payroll.run:approve', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },

    reopenRun: {
      method: 'POST',
      path: '/runs/:runId/reopen',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: reopenRunBodySchema,
      responses: {
        200: runDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Payment has already been initiated — needs a correction, not an unlock. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Authorised reopen: invalidates the approval and requires recalculation',
      metadata: routeMeta(['payroll.run:reopen', 'payroll:read'], { ...RUN_STEP_AI, salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/payroll' },
);
